import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'

import { Duration } from '~/components/atoms/duration'
import { cn } from '~/lib/utils'

import { useDraggable } from '@dnd-kit/core'
import { Clock } from 'lucide-react'
import type { TimeSlot } from '../../templates/booking-stepper'
import { TIME_SLOT_HEIGHT, type TimeslotBounds } from './helpers'
import { ResizeHandle } from './resize-handle'

interface DraggableTimeslotProps {
	bounds: TimeslotBounds
	isTransforming: boolean
	isAvailable?: boolean
	notAvailableMsg?: string
	value: TimeSlot
}

export const DraggableTimeslot = ({
	isAvailable,
	isTransforming,
	bounds,
	notAvailableMsg,
	value,
}: DraggableTimeslotProps) => {
	const { t } = useTranslation()

	const body = useDraggable({ id: 'draggable-timeslot' })
	const resizeTop = useDraggable({ id: 'draggable-timeslot-resize-top' })
	const resizeBottom = useDraggable({ id: 'draggable-timeslot-resize-bottom' })

	const getStyle = () => {
		let top = bounds.startY
		let height = bounds.height

		if (body.transform) {
			top += body.transform.y
		}

		if (resizeTop.transform) {
			top += resizeTop.transform.y
			height -= resizeTop.transform.y
		}

		if (resizeBottom.transform) {
			height = Math.max(
				bounds.height + resizeBottom.transform.y,
				TIME_SLOT_HEIGHT,
			)
		}

		return {
			height,
			top,
			transition: isTransforming ? 'none' : undefined,
		}
	}

	const bookingDuration = useMemo(() => {
		const start = dateTimeUtils.utils.getTimeAsMinutes(value.start!)
		const end = dateTimeUtils.utils.getTimeAsMinutes(value.end!)

		return end - start
	}, [value])

	return (
		<div
			ref={body.setNodeRef}
			{...body.listeners}
			{...body.attributes}
			className={cn(
				'absolute left-0 flex w-full cursor-move touch-none flex-col justify-between border border-primary bg-primary bg-primary text-white transition-[background-color,top]',
				{ 'bg-slate-300/50 text-primary': !isAvailable },
			)}
			style={getStyle()}
		>
			<ResizeHandle
				position='top'
				draggable={resizeTop}
			/>

			<ResizeHandle
				position='bottom'
				draggable={resizeBottom}
			/>

			<div className='absolute bottom-0 ml-1 flex flex-row items-center gap-x-1 font-medium text-xs'>
				<Clock size={12} />

				<Duration duration={bookingDuration} />
			</div>

			{!isAvailable && (
				<p className='absolute right-0 bottom-0 mr-2 font-bold text-xs'>
					{t([
						`errors:api.${notAvailableMsg}`,
						'booking:stepper.timeScheduler.unavailable',
					])}
				</p>
			)}
		</div>
	)
}
